import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';

import { inExperiment, useUser } from '@reverbdotcom/commons/src/user_hooks';

import {
  buildSellingSection,
  buildBuyingSection,
  buildFinancesSection,
  buildMyAccountSection,
  buildExploreSection,
  buildJoinReverbSection,
  ILinkProps,
} from './reverb_header_menu_data';
import ReverbHeaderMenuSection from './reverb_header_menu_section';
import { ReverbHeaderUserMenuData } from '../../../schemas/rql';
import { RCIcon } from '@reverbdotcom/cadence/components';
import { BarsAltIcon } from '@reverbdotcom/cadence/icons/react';
import experiments from '@reverbdotcom/commons/src/experiments';

interface IProps {
  me: ReverbHeaderUserMenuData.Fragment;
}

const DEFAULT_AVATAR = 'https://reverb-dev-res.cloudinary.com/image/upload/s--BtEvm2y3--/v1546540852/default--rounded_misa1r.png';

interface IMobileMenuSectionProps {
  data: {
    key: string;
    title: string;
    links: ILinkProps[]
  }
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

export function MobileMenuSection({ data, onClick }: IMobileMenuSectionProps) {
  const { key, title, links } = data;

  return (
    <ReverbHeaderMenuSection key={key} title={title} links={links} onClick={onClick}/>
  );
}

function ReverbHeaderMobileMenu({ me }: IProps) {
  const user = useUser();
  const [open, setOpen] = React.useState(false);
  const classes = classNames(
    'reverb-header__mobile-menu',
    { 'reverb-header__mobile-menu--active': open },
  );

  const avatar = user.avatarUrl || DEFAULT_AVATAR;
  const { slug: shopSlug, paymentMethod: shopPaymentMethod } = me?.shop || {};

  const signedIn = !user.loggedOut;
  const inElevatedFavoritesNavExperiment = inExperiment(user, experiments.FAVORITES_ELEVATED_NAVIGATION);

  return (
    <>
      <li className="reverb-header__show-only-on-mobile">
        <button
          type="button"
          className={classNames(
            {
              'reverb-header__mobile-menu-toggle': !inElevatedFavoritesNavExperiment,
              'reverb-header__experimental-mobile-menu-toggle': inElevatedFavoritesNavExperiment,
            },
          )}
          onClick={() => setOpen(true)}
        >
          {inElevatedFavoritesNavExperiment ? (
            <RCIcon svgComponent={BarsAltIcon} />
          ) : (
            I18n.t('discovery.reverbHeader.mobileMenu.badge')
          )}
        </button>
      </li>
      <div className={classes}>
        <button
          type="button"
          className="reverb-header__mobile-menu__close"
          onClick={() => setOpen(false)}
        >
          {I18n.t('discovery.reverbHeader.mobileMenu.close')}
        </button>
        <div className="reverb-header__mobile-menu__header reverb-header__mobile-menu__header--centered">
          {signedIn && (
            <>
              <div className="reverb-header__avatar" title={user.name}>
                <img width="64" height="64" src={avatar} alt="Avatar" />
              </div>
              <div className="reverb-header__name reverb-header__name--mobile" title={user.name}>
                {user.name}
              </div>
            </>
          )}
        </div>
        <div className="g-container">
          <div className="g-col-6">
            <MobileMenuSection data={buildExploreSection(user)} onClick={() => setOpen(false)} />
            {signedIn && (
              <>
                <MobileMenuSection data={buildBuyingSection()} onClick={() => setOpen(false)} />
                <MobileMenuSection data={buildMyAccountSection({ locale: user.locale })} onClick={() => setOpen(false)} />
              </>
            )}
          </div>
          <div className="g-col-6">
            {user.loggedOut && <MobileMenuSection data={buildJoinReverbSection(user.locale)} onClick={() => setOpen(false)} />}
            {signedIn && <MobileMenuSection data={buildSellingSection(user, shopSlug)} onClick={() => setOpen(false)} />}
            {shopPaymentMethod && <MobileMenuSection data={buildFinancesSection(shopPaymentMethod)} onClick={() => setOpen(false)} />}
          </div>
        </div>
      </div>
    </>
  );
}

export default ReverbHeaderMobileMenu;
